import Controller from "@ember/controller";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { debounce } from "@ember/runloop";
import { service } from "@ember/service";
import { htmlSafe } from "@ember/template";
import { tracked } from "@glimmer/tracking";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import UppyComposerUpload from "discourse/lib/uppy/composer-upload";
import { uploadIcon } from "discourse/lib/uploads";
import { i18n } from "discourse-i18n";
import { uploadFromResource, uploadImageFile, uploadPreviewUrl } from "../lib/dec-image-upload";
import {
  normalizeHttpUrlInput as normalizedUrlInput,
  safeHttpUrl,
  safeNavigationUrl,
} from "../lib/dec-safe-url";

const RSVP_STATUSES = [
  { status: "going", label: "Going" },
  { status: "interested", label: "Interested" },
  { status: "not_going", label: "Not going" },
  { status: "waitlisted", label: "Waitlisted" },
];

const RSVP_ACTION_STATUSES = RSVP_STATUSES.filter((item) => item.status !== "waitlisted");
const RSVP_FACEPILE_STATUSES = ["going", "interested", "waitlisted"];
const RSVP_FACEPILE_LIMIT = 8;
const CHAT_TRANSCRIPT_PAGE_SIZE = 20;

function extractErrorMessage(error, fallback = "RSVP could not be saved. Try again.") {
  const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON;
  const errors = responseJSON?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(" ");
  }

  if (typeof errors === "string") {
    return errors;
  }

  if (responseJSON?.error) {
    return responseJSON.error;
  }

  if (responseJSON?.message) {
    return responseJSON.message;
  }

  return fallback;
}

function normalizeEventResponse(response) {
  return response?.event || response || null;
}

function normalizeChatChannelResponse(response) {
  const channel = response?.channel || response;
  if (!channel?.id) {
    return null;
  }

  const membership = channel.current_user_membership || channel.membership || {};
  const meta = channel.meta || {};

  return {
    id: channel.id,
    title: channel.title || channel.name || "Chat channel",
    slug: channel.slug || "",
    status: channel.status || "",
    canJoin: meta.can_join_chat_channel !== false,
    membership,
    following: membership.following === true,
  };
}

function normalizeChatUser(user) {
  if (!user) {
    return null;
  }

  return {
    id: user.id,
    username: user.username || "",
    name: user.name || "",
    avatar_template: user.avatar_template,
  };
}

function normalizeChatMessage(message) {
  return {
    id: message?.id,
    message: message?.message || message?.excerpt || "",
    cooked: message?.cooked || "",
    excerpt: message?.excerpt || "",
    createdAt: message?.created_at,
    deletedAt: message?.deleted_at,
    user: normalizeChatUser(message?.user),
  };
}

function normalizeChatTranscriptResponse(threadResponse, messagesResponse) {
  const thread = threadResponse?.thread || null;
  const transcriptMessages = Array.isArray(messagesResponse?.messages)
    ? messagesResponse.messages
    : [];
  const seenMessageIds = new Set();
  const messages = [];

  [thread?.original_message, ...transcriptMessages].forEach((message) => {
    if (!message?.id || seenMessageIds.has(message.id)) {
      return;
    }

    seenMessageIds.add(message.id);
    messages.push(normalizeChatMessage(message));
  });

  return {
    thread: thread
      ? {
          id: thread.id,
          title: thread.title || "",
          status: thread.status || "",
          replyCount: thread.reply_count || 0,
        }
      : null,
    messages,
    canLoadMorePast: messagesResponse?.meta?.can_load_more_past === true,
    canLoadMoreFuture: messagesResponse?.meta?.can_load_more_future === true,
  };
}

function normalizePlaceOption(place) {
  if (!place?.id || !place?.name) {
    return null;
  }

  const address = place.address || place.formatted_address || "";
  return {
    ...place,
    id: place.id,
    name: place.name,
    address,
    label: [place.name, address].filter(Boolean).join(" - "),
  };
}

function pad(value) {
  return `${value}`.padStart(2, "0");
}

// See safeDateFormat in discourse-events-calendar.js — kept identical
// across controllers; resolves to Intl.DateTimeFormat(undefined, options)
// with the caller's ASCII fallback on any error.
function safeDateFormat(date, options, fallback) {
  if (!date) {
    return fallback;
  }
  try {
    const dateObject = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObject.getTime())) {
      return fallback;
    }
    return new Intl.DateTimeFormat(undefined, options).format(dateObject);
  } catch {
    return fallback;
  }
}

function fallbackDate(date) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function fallbackTime(date) {
  let hours = date.getHours();
  const minutes = pad(date.getMinutes());
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${period}`;
}

function dateString(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseDateString(value) {
  if (!value) {
    return new Date();
  }

  const [year, month, day] = value.split("-").map((part) => parseInt(part, 10));

  if (!year || !month || !day) {
    return new Date();
  }

  return new Date(year, month - 1, day);
}

function dateIsBefore(leftValue, rightValue) {
  return parseDateString(leftValue) < parseDateString(rightValue);
}

function defaultRecurrenceUntil(dateValue) {
  const date = parseDateString(dateValue);
  date.setMonth(date.getMonth() + 3);
  return dateString(date);
}

const RECURRENCE_WEEKDAYS = [
  { code: "MO", label: "Mon", day: 1 },
  { code: "TU", label: "Tue", day: 2 },
  { code: "WE", label: "Wed", day: 3 },
  { code: "TH", label: "Thu", day: 4 },
  { code: "FR", label: "Fri", day: 5 },
  { code: "SA", label: "Sat", day: 6 },
  { code: "SU", label: "Sun", day: 0 },
];

function weekdayCodeForDate(dateValue) {
  const day = parseDateString(dateValue).getDay();
  return RECURRENCE_WEEKDAYS.find((weekday) => weekday.day === day)?.code || "MO";
}

function weeklyRrule({ byday, interval, until }) {
  const parts = [
    "FREQ=WEEKLY",
    `INTERVAL=${normalizedInterval(interval)}`,
    `BYDAY=${byday.join(",")}`,
  ];

  if (until) {
    const untilDate = parseDateString(until);
    untilDate.setHours(23, 59, 59, 0);
    parts.push(`UNTIL=${untilDate.toISOString().replace(/[-:]/g, "").replace(".000", "")}`);
  }

  return parts.join(";");
}

function parseWeeklyRrule(value) {
  if (!value) {
    return null;
  }

  const parts = `${value}`.split(";").reduce((memo, part) => {
    const [key, rawValue] = part.split("=");
    if (key && rawValue) {
      memo[key.toUpperCase()] = rawValue;
    }
    return memo;
  }, {});

  if (parts.FREQ !== "WEEKLY") {
    return null;
  }

  const byday = RECURRENCE_WEEKDAYS.map((weekday) => weekday.code).filter((code) =>
    (parts.BYDAY || "").split(",").includes(code)
  );

  if (byday.length === 0) {
    return null;
  }

  return {
    byday,
    interval: normalizedInterval(parts.INTERVAL || 1),
    until: rruleUntilDate(parts.UNTIL),
  };
}

function rruleUntilDate(value) {
  if (!value) {
    return "";
  }

  const match = `${value}`.match(/^(\d{4})(\d{2})(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
}

function timeString(date) {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function timeLabel(value) {
  const [hours, minutes] = value.split(":").map((part) => parseInt(part, 10));
  const date = new Date(2000, 0, 1, hours, minutes);

  return fallbackTime(date);
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const TIMEZONE_ALIASES = {
  PDT: "America/Los_Angeles",
  PST: "America/Los_Angeles",
  MDT: "America/Denver",
  MST: "America/Denver",
  CDT: "America/Chicago",
  CST: "America/Chicago",
  EDT: "America/New_York",
  EST: "America/New_York",
};

function isValidTimeZone(timezone) {
  if (!timezone) {
    return false;
  }

  try {
    Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

function normalizeTimeZone(timezone, fallback = browserTimeZone()) {
  const candidate = TIMEZONE_ALIASES[timezone] || timezone;

  if (isValidTimeZone(candidate)) {
    return candidate;
  }

  if (isValidTimeZone(fallback)) {
    return fallback;
  }

  return "UTC";
}

function timezoneOptions(selectedTimezone) {
  const normalizedSelectedTimezone = normalizeTimeZone(selectedTimezone, browserTimeZone());
  const preferredZones = [
    normalizedSelectedTimezone,
    browserTimeZone(),
    "UTC",
    "America/Los_Angeles",
    "America/New_York",
    "Europe/London",
    "Europe/Berlin",
    "Asia/Tokyo",
    "Australia/Sydney",
  ].filter(Boolean);
  const supportedZones =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const zones = [...new Set([...preferredZones, ...supportedZones])];

  return zones.map((zone) => ({
    value: zone,
    label: zone.replace(/_/g, " "),
    isSelected: zone === normalizedSelectedTimezone,
  }));
}

function attendeeChipName(attendee) {
  const displayName = attendee.name || attendee.username || "Member";
  return displayName.trim();
}

function eventDateInput(value) {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? "" : dateString(date);
}

function eventTimeInput(value) {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? "" : timeString(date);
}

function dateWithTime(dateValue, timeValue) {
  const [year, month, day] = dateValue.split("-").map((part) => parseInt(part, 10));
  const [hours, minutes] = timeValue.split(":").map((part) => parseInt(part, 10));

  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

function nextDayString(value) {
  const date = dateWithTime(value, "00:00");
  date.setDate(date.getDate() + 1);
  return dateString(date);
}

function sameLocalDate(left, right) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function eventDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return safeDateFormat(
    date,
    {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    fallbackDate(date)
  );
}

function eventDatePart(value, options, fallback = "") {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return safeDateFormat(date, options, fallback);
}

function eventTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return safeDateFormat(
    date,
    {
      hour: "numeric",
      minute: "2-digit",
    },
    fallbackTime(date)
  );
}

function eventDateTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return `${eventDate(value)} at ${eventTime(value)}`;
}

function eventTimeRange(startsAtValue, endsAtValue, allDay = false) {
  if (allDay) {
    return "All day";
  }

  if (!startsAtValue) {
    return "";
  }

  if (!endsAtValue) {
    return eventTime(startsAtValue);
  }

  const startsAt = new Date(startsAtValue);
  const endsAt = new Date(endsAtValue);

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return "";
  }

  if (sameLocalDate(startsAt, endsAt)) {
    return `${eventTime(startsAtValue)} - ${eventTime(endsAtValue)}`;
  }

  return `${eventDateTime(startsAtValue)} - ${eventDateTime(endsAtValue)}`;
}

function eventIsPast(event) {
  if (event?.is_past === true) {
    return true;
  }

  const endsAt = new Date(event?.ends_at || event?.starts_at);

  return !Number.isNaN(endsAt.getTime()) && endsAt < new Date();
}

function eventIsOngoing(event) {
  if (event?.is_ongoing === true) {
    return true;
  }

  const startsAt = new Date(event?.starts_at);
  const endsAt = new Date(event?.ends_at || event?.starts_at);
  const now = new Date();

  return (
    !Number.isNaN(startsAt.getTime()) &&
    !Number.isNaN(endsAt.getTime()) &&
    startsAt <= now &&
    endsAt >= now
  );
}

function statusLabel(status) {
  if (status === "pending_approval") {
    return "Pending approval";
  }

  if (status === "rejected") {
    return "Rejected";
  }

  if (status === "canceled") {
    return "Canceled";
  }

  if (status === "archived") {
    return "Archived";
  }

  return RSVP_STATUSES.find((candidate) => candidate.status === status)?.label || "";
}

function visibilityLabel(visibility, hidden) {
  if (visibility === "staff_visibility") {
    return "Staff only";
  }

  if (visibility === "private_visibility") {
    return "Private invite-only";
  }

  return hidden ? "Unlisted" : "Public";
}

function visibilityModeFromFields(visibility, hidden) {
  if (visibility === "private_visibility") {
    return "private";
  }

  if (visibility === "staff_visibility") {
    return "staff";
  }

  return hidden ? "unlisted" : "public";
}

function visibilityValueForMode(mode) {
  if (mode === "private") {
    return "private_visibility";
  }

  if (mode === "staff") {
    return "staff_visibility";
  }

  return "public_visibility";
}

function hiddenValueForMode(mode) {
  return ["private", "unlisted"].includes(mode);
}

function recurrenceLabel(rule) {
  switch (rule) {
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "monthly":
      return "Monthly";
    case "yearly":
      return "Yearly";
    default:
      return "None";
  }
}

function recurrenceUnit(rule, interval) {
  const singular =
    {
      daily: "day",
      weekly: "week",
      monthly: "month",
      yearly: "year",
    }[rule] || "period";

  return interval === 1 ? singular : `${singular}s`;
}

function normalizedInterval(value) {
  const interval = parseInt(value, 10);
  return Number.isFinite(interval) && interval > 0 ? interval : 1;
}

function editorValue(valueOrEvent) {
  if (typeof valueOrEvent === "string") {
    return valueOrEvent;
  }

  return valueOrEvent?.target?.value || "";
}

function decodeHtmlEntities(value) {
  return `${value || ""}`
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

function excerptFromCooked(cooked, fallback = "") {
  const text = decodeHtmlEntities(`${cooked || ""}`.replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim();
  const excerpt = text || fallback.trim();

  if (excerpt.length <= 260) {
    return excerpt;
  }

  return `${excerpt.slice(0, 257).trim()}...`;
}

function plainTextExcerpt(value, limit = 520) {
  const text = `${value || ""}`
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/<[^>]*>/g, " ")
    .replace(/[#>*_`~|\[\]()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return text.length <= limit ? text : `${text.slice(0, limit - 3).trim()}...`;
}

function normalizeDiscussionPost(response, raw) {
  const post = response?.post || response || {};
  const username = post.username || post.user?.username || "";

  return {
    id: post.id,
    username,
    name: post.name || post.user?.name || "",
    avatar_template: post.avatar_template || post.user?.avatar_template,
    cooked: post.cooked || "",
    excerpt: excerptFromCooked(post.cooked, raw),
    postNumber: post.post_number || post.postNumber,
    createdAt: post.created_at || new Date().toISOString(),
  };
}

function userUrl(username) {
  return username ? `/u/${username}` : "";
}

function eventRoleDisplayName(role) {
  return role?.display_name || role?.name || role?.username || "";
}

function eventRoleInitials(role) {
  return eventRoleDisplayName(role)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function eventRoleUserUrl(role) {
  return role?.username ? userUrl(role.username) : "";
}

function participantNamesForRoles(roles) {
  return roles
    .map((role) => (role?.username ? `@${role.username}` : eventRoleDisplayName(role)))
    .filter(Boolean)
    .join(", ");
}

function participantNames(value) {
  return `${value || ""}`
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}

function participantRoleRows(value, role, positionOffset = 0) {
  return participantNames(value).map((displayName, index) => ({
    role,
    display_name: displayName,
    position: positionOffset + index,
  }));
}

function ownerParticipantName(event) {
  return event?.owner?.username ? `@${event.owner.username}` : "";
}

function roleSearchTerm(value) {
  return `${value || ""}`
    .split(",")
    .pop()
    .trim()
    .replace(/^@/, "");
}

function normalizeUserOption(user) {
  if (!user?.username) {
    return null;
  }

  return {
    username: user.username,
    name: user.name || "",
    value: `@${user.username}`,
    label: user.name ? `${user.name} (@${user.username})` : `@${user.username}`,
  };
}

function chatMessageBody(message) {
  if (message?.deletedAt) {
    return "Message deleted.";
  }

  const body = `${message?.message || message?.excerpt || ""}`.trim();
  return body || "Open chat to view this message.";
}

function locationUrl(location) {
  const value = location?.slug || location?.id || location?.name || location;
  return value ? `/calendar/locations/${encodeURIComponent(value)}` : "";
}

function locationValue(location) {
  return `${location?.id || location?.slug || location?.name || ""}`;
}

function invitationDisplayName(invitation) {
  if (invitation?.user) {
    return invitation.user.name || invitation.user.username;
  }

  if (invitation?.group) {
    return invitation.group.full_name || invitation.group.name;
  }

  return "Invitee";
}

export default class DiscourseEventsCalendarEventController extends Controller {
  @service currentUser;
  @service site;
  @service siteSettings;

  @tracked event = null;
  @tracked rooms = [];
  @tracked locations = [];
  @tracked categories = [];
  @tracked attendeeList = [];
  @tracked canViewAllAttendeeStatuses = false;
  @tracked attendeeLoadError = "";
  @tracked invitations = [];
  @tracked inviteUsernames = "";
  @tracked inviteGroupNames = "";
  @tracked invitationNotice = "";
  @tracked invitationError = "";
  @tracked isLoadingInvitations = false;
  @tracked isSavingInvitations = false;
  @tracked removingInvitationId = null;
  @tracked discussionTopic = null;
  @tracked discussionPosts = [];
  @tracked discussionLoadError = "";
  @tracked discussionReply = "";
  @tracked discussionReplyError = "";
  @tracked discussionReplyNotice = "";
  @tracked isSavingDiscussionReply = false;
  @tracked chatChannel = null;
  @tracked chatChannelLoadError = "";
  @tracked chatChannelNotice = "";
  @tracked chatChannelError = "";
  @tracked isUpdatingChatChannelMembership = false;
  @tracked chatThread = null;
  @tracked chatMessages = [];
  @tracked chatCanLoadMorePast = false;
  @tracked chatCanLoadMoreFuture = false;
  @tracked chatLoadError = "";
  @tracked isRefreshingChatTranscript = false;
  @tracked isLoadingAttendees = false;
  @tracked loadError = "";
  @tracked rsvpError = "";
  @tracked rsvpNotice = "";
  @tracked isSavingRsvp = false;
  @tracked selectedRsvpOccurrenceKey = "series";
  @tracked isEditing = false;
  @tracked isSavingEvent = false;
  @tracked isCancelingEvent = false;
  @tracked isUncancelingEvent = false;
  @tracked isCreatingTopic = false;
  @tracked isCreatingChat = false;
  @tracked isUpdatingStatus = false;
  @tracked editError = "";
  @tracked editNotice = "";
  @tracked editTitle = "";
  @tracked editImageUpload = null;
  @tracked editImageUploadId = "";
  @tracked editImageUploadError = "";
  @tracked isUploadingEditImage = false;
  @tracked showEditAiImagePrompt = false;
  @tracked editAiImagePrompt = "";
  @tracked pendingEditAiImageUpload = null;
  @tracked isComposingEditAiImagePrompt = false;
  @tracked isGeneratingEditAiImage = false;
  @tracked editDate = "";
  @tracked editStartTime = "";
  @tracked editEndTime = "";
  @tracked editAllDay = false;
  @tracked editEndsNextDay = false;
  @tracked editLocation = "";
  @tracked editPlaceQuery = "";
  @tracked editPlaceId = "";
  @tracked editPlaceOptions = [];
  @tracked isLoadingEditPlaces = false;
  @tracked editSelectedLocationId = "";
  @tracked editRoomId = "";
  @tracked editRoomLocationApplied = "";
  @tracked editCategoryId = "";
  @tracked editTagNamesCsv = "";
  @tracked editHostNames = "";
  @tracked editCoHostNames = "";
  @tracked editSpeakerNames = "";
  @tracked editWithNames = "";
  @tracked showEditPeopleDetails = false;
  @tracked editRoleUserOptions = [];
  @tracked isLoadingEditRoleUsers = false;
  @tracked editDescription = "";
  @tracked editTimezone = "";
  @tracked editCapacity = "";
  @tracked editVisibility = "public";
  @tracked editRecurrenceRule = "none";
  @tracked editRecurrenceInterval = "1";
  @tracked editRecurrenceUntil = "";
  @tracked editRecurrenceByday = "";
  @tracked editHadSupportedExternalRrule = false;
  @tracked editRsvpEnabled = true;
  @tracked editRsvpMode = "native";
  @tracked editExternalRsvpUrl = "";
  @tracked editRsvpAllowInterested = true;
  editDescriptionFileUploadElementId = "event-edit-description-file-uploader";
  editDescriptionUpload = null;
  editDescriptionUploadElement = null;
  editDescriptionUploadSetup = false;
  editPlaceRequestId = 0;
  editRoleUserRequestId = 0;
  placeMap = null;
  placeMapElement = null;

  syncEventFromModel(model) {
    this.event = model?.event || null;
    this.rooms = model?.rooms || [];
    this.locations = model?.locations || [];
    this.categories = model?.categories || [];
    this.attendeeList = model?.attendees || this.event?.attendees || [];
    this.canViewAllAttendeeStatuses = model?.canViewAllAttendeeStatuses === true;
    this.attendeeLoadError = model?.attendeeLoadError || "";
    this.invitations = [];
    this.inviteUsernames = "";
    this.inviteGroupNames = "";
    this.invitationNotice = "";
    this.invitationError = "";
    this.isLoadingInvitations = false;
    this.isSavingInvitations = false;
    this.removingInvitationId = null;
    this.discussionTopic = model?.discussionTopic || null;
    this.discussionPosts = model?.discussionPosts || [];
    this.discussionLoadError = model?.discussionLoadError || "";
    this.discussionReply = "";
    this.discussionReplyError = "";
    this.discussionReplyNotice = "";
    this.isSavingDiscussionReply = false;
    this.chatChannel = model?.chatChannel || null;
    this.chatChannelLoadError = model?.chatChannelLoadError || "";
    this.chatChannelNotice = "";
    this.chatChannelError = "";
    this.isUpdatingChatChannelMembership = false;
    this.chatThread = model?.chatThread || null;
    this.chatMessages = model?.chatMessages || [];
    this.chatCanLoadMorePast = model?.chatCanLoadMorePast === true;
    this.chatCanLoadMoreFuture = model?.chatCanLoadMoreFuture === true;
    this.chatLoadError = model?.chatLoadError || "";
    this.isRefreshingChatTranscript = false;
    this.isLoadingAttendees = false;
    this.loadError = model?.loadError || "";
    this.rsvpError = "";
    this.rsvpNotice = "";
    this.isSavingRsvp = false;
    this.selectedRsvpOccurrenceKey = this.defaultRsvpOccurrenceKey;
    this.isEditing = false;
    this.isSavingEvent = false;
    this.isCancelingEvent = false;
    this.isUncancelingEvent = false;
    this.isCreatingTopic = false;
    this.isCreatingChat = false;
    this.isUpdatingStatus = false;
    this.isApprovingEvent = false;
    this.isRejectingEvent = false;
    this.editError = "";
    this.editNotice = "";
    this.resetEditFields();
    this.loadInvitations();
  }

  get hasEvent() {
    return Boolean(this.event);
  }

  get canManageInvitations() {
    return this.event?.can_manage_invitations === true;
  }

  get showInvitationsPanel() {
    return this.canManageInvitations && this.event?.visibility === "private_visibility";
  }

  get hasInvitations() {
    return this.invitations.length > 0;
  }

  get invitationRows() {
    return this.invitations.map((invitation) => ({
      ...invitation,
      label: invitationDisplayName(invitation),
      kind: invitation.user ? "User" : "Group",
    }));
  }

  get cannotSaveInvitations() {
    return this.isSavingInvitations || !(this.inviteUsernames.trim() || this.inviteGroupNames.trim());
  }

  get editDescriptionUploadIcon() {
    return uploadIcon(this.currentUser?.staff, this.siteSettings);
  }

  get statusLabel() {
    if (!this.event) {
      return "";
    }

    if (this.event.status === "draft") {
      return "Draft";
    }

    if (
      this.event.status === "pending_approval" ||
      this.event.status === "rejected" ||
      this.event.status === "canceled" ||
      this.event.status === "archived"
    ) {
      return statusLabel(this.event.status);
    }

    if (eventIsPast(this.event)) {
      return "Past";
    }

    if (eventIsOngoing(this.event)) {
      return "In progress";
    }

    return "Upcoming";
  }

  get dateLabel() {
    return eventDate(this.event?.starts_at);
  }

  get ownerUrl() {
    return userUrl(this.event?.owner?.username);
  }

  get hasOwner() {
    return Boolean(
      this.event?.owner?.username &&
        !this.hasEventRoles &&
        !this.event?.host_display_name
    );
  }

  get ownerHostRole() {
    const hostDisplayName = this.event?.host_display_name;
    if (hostDisplayName) {
      return {
        role: "host",
        display_name: hostDisplayName,
        displayName: hostDisplayName,
        initials: eventRoleInitials({ display_name: hostDisplayName }),
        isSourceHost: true,
      };
    }

    const owner = this.event?.owner;
    if (!owner?.username) {
      return null;
    }

    return {
      role: "host",
      user_id: owner.id,
      username: owner.username,
      name: owner.name,
      avatar_template: owner.avatar_template,
      display_name: owner.name || owner.username,
      displayName: owner.name || owner.username,
      initials: eventRoleInitials(owner),
      userUrl: userUrl(owner.username),
      isDefaultHost: true,
    };
  }

  get eventRoleGroups() {
    const roles = Array.isArray(this.event?.event_roles) ? this.event.event_roles : [];
    const hosts = roles.filter((role) => ["host", "co_host"].includes(role.role));
    const speakers = roles.filter((role) => role.role === "speaker");
    const withRoles = roles.filter((role) => role.role === "custom_host");
    const displayHosts = hosts.length > 0 ? hosts : [this.ownerHostRole].filter(Boolean);

    return [
      { label: "Hosted by", roles: displayHosts },
      { label: "Speakers", roles: speakers },
      { label: "Presented with", roles: withRoles },
    ]
      .filter((group) => group.roles.length > 0)
      .map((group) => ({
        label: group.label,
        roles: group.roles.map((role) => ({
          ...role,
          displayName: eventRoleDisplayName(role),
          initials: eventRoleInitials(role),
          userUrl: eventRoleUserUrl(role),
          avatar_template: role.avatar_template,
        })),
      }));
  }

  get hasEventRoles() {
    return this.eventRoleGroups.length > 0;
  }

  get hasCategory() {
    return Boolean(this.event?.category?.name);
  }

  get categoryName() {
    return this.event?.category?.name || "";
  }

  get categoryUrl() {
    return this.event?.category?.slug && this.event?.category?.id
      ? `/c/${this.event.category.slug}/${this.event.category.id}`
      : "";
  }

  get hasCategoryVisual() {
    return Boolean(this.event?.category?.icon_url || this.event?.category?.emoji);
  }

  get categoryEmojiText() {
    const emoji = this.event?.category?.emoji;
    if (!emoji) {
      return "";
    }

    return /^[a-z0-9_+-]+$/i.test(emoji) ? `:${emoji}:` : emoji;
  }

  get categoryBadgeStyle() {
    const color = this.event?.category?.color;
    const textColor = this.event?.category?.text_color;
    const declarations = [];

    if (color) {
      declarations.push(`--dec-category-color: #${color}`);
    }

    if (textColor) {
      declarations.push(`--dec-category-text-color: #${textColor}`);
    }

    return declarations.length ? htmlSafe(declarations.join("; ")) : "";
  }

  get editRoleUserSuggestionOptions() {
    return this.editRoleUserOptions;
  }

  get showEditAdvancedPeopleFields() {
    return Boolean(
      this.showEditPeopleDetails ||
        this.editCoHostNames.trim() ||
        this.editSpeakerNames.trim() ||
        this.editWithNames.trim()
    );
  }

  get editPeopleToggleLabel() {
    return this.showEditAdvancedPeopleFields
      ? "Hide additional people"
      : "Add co-hosts, speakers, or partners";
  }

  get timeLabel() {
    return eventTimeRange(
      this.event?.starts_at,
      this.event?.ends_at,
      this.event?.all_day
    );
  }

  get hasEventImage() {
    return Boolean(this.event?.image_url);
  }

  get editImagePreviewUrl() {
    return uploadPreviewUrl(this.editImageUpload);
  }

  get pendingEditAiImagePreviewUrl() {
    return uploadPreviewUrl(this.pendingEditAiImageUpload);
  }

  get hasEditImageUpload() {
    return Boolean(this.editImagePreviewUrl);
  }

  get hasPendingEditAiImageUpload() {
    return Boolean(this.pendingEditAiImagePreviewUrl);
  }

  get canUseAiImageGeneration() {
    return this.model?.ai_image_generation_available === true;
  }

  get cannotComposeEditAiImagePrompt() {
    return this.isComposingEditAiImagePrompt || this.isGeneratingEditAiImage;
  }

  get cannotGenerateEditAiImage() {
    return (
      this.isComposingEditAiImagePrompt ||
      this.isGeneratingEditAiImage ||
      this.editAiImagePrompt.trim().length === 0
    );
  }

  get editAiImagePromptToggleLabel() {
    if (this.isComposingEditAiImagePrompt) {
      return "Preparing prompt...";
    }

    return this.editAiImagePrompt ? "Edit prompt" : "Generate with Discourse AI";
  }

  get eventImageAlt() {
    return this.event?.title ? `Image for ${this.event.title}` : "Event image";
  }

  get eventPosterMonthLabel() {
    return eventDatePart(this.event?.starts_at, { month: "short" }, "Date");
  }

  get eventPosterDayLabel() {
    return eventDatePart(this.event?.starts_at, { day: "2-digit" }, "TBD");
  }

  get eventPosterWeekdayLabel() {
    return eventDatePart(this.event?.starts_at, { weekday: "short" }, "");
  }

  get eventPosterCategoryLabel() {
    return this.categoryName || "Community event";
  }

  get eventPosterTimeLabel() {
    return this.timeLabel || i18n("discourse_events_calendar.labels.time_tbd");
  }

  get eventPosterLocationLabel() {
    return this.event?.place?.name || this.event?.location || this.roomName || "Venue TBD";
  }

  get placesEnabled() {
    return this.model?.placesEnabled === true || this.siteSettings?.discourse_places_enabled === true;
  }

  get rsvpGloballyEnabled() {
    return this.siteSettings?.discourse_events_calendar_rsvp_globally_enabled !== false;
  }

  get hasPlace() {
    return Boolean(this.event?.place?.name);
  }

  get placeUrl() {
    if (this.event?.place?.url) {
      return safeNavigationUrl(this.event.place.url);
    }

    return this.event?.place?.slug ? `/places/${encodeURIComponent(this.event.place.slug)}` : "";
  }

  get hasPlaceUrl() {
    return Boolean(this.placeUrl);
  }

  get placeAddress() {
    return this.event?.place?.address || this.event?.place?.formatted_address || "";
  }

  get venueName() {
    if (this.event?.venue_type === "virtual" || this.event?.meeting_url) {
      return ["Meeting", "Online event"].includes(this.event?.location)
        ? "Virtual event"
        : this.event?.location || "Virtual event";
    }

    return (
      this.event?.place?.name ||
      this.event?.location_record?.name ||
      this.event?.location ||
      this.roomLocation ||
      ""
    );
  }

  get hasVenue() {
    return Boolean(this.venueName);
  }

  get venueUrl() {
    if (this.event?.venue_type === "virtual" && this.event?.meeting_url) {
      return safeHttpUrl(this.event.meeting_url);
    }

    if (this.hasPlaceUrl) {
      return this.placeUrl;
    }

    if (this.event?.custom_location_url) {
      return safeHttpUrl(this.event.custom_location_url);
    }

    return this.locationUrl;
  }

  get hasVenueUrl() {
    return Boolean(this.venueUrl);
  }

  get venueAddress() {
    const address =
      this.placeAddress ||
      this.event?.location_record?.address ||
      (this.event?.venue_type === "virtual" ? "" : this.event?.location) ||
      "";

    return address && address !== this.venueName ? address : "";
  }

  get hasVenueAddress() {
    return Boolean(this.venueAddress);
  }

  get hasEditPlaceOptions() {
    return this.editPlaceOptions.length > 0;
  }

  get placeMapMarkers() {
    const place = this.event?.place;
    const latitude = parseFloat(place?.latitude);
    const longitude = parseFloat(place?.longitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return [];
    }

    return [
      {
        id: place.id,
        slug: place.slug,
        label: place.name,
        latitude,
        longitude,
      },
    ];
  }

  get hasPlaceMap() {
    return this.placesEnabled && this.placeMapMarkers.length > 0;
  }

  get hasEventDetailAside() {
    // The aside rail always carries the event facts (date/time/venue), so it
    // is always rendered; the map, RSVP, and host cards join it when present.
    return true;
  }

  get eventDetailOverviewClass() {
    return [
      "discourse-events-calendar__event-detail-overview",
      this.hasEventDetailAside
        ? "discourse-events-calendar__event-detail-overview--with-aside"
        : "",
    ]
      .filter(Boolean)
      .join(" ");
  }

  get roomName() {
    return this.event?.room?.name || this.event?.room_name || "";
  }

  get roomLocation() {
    return this.event?.room?.location || this.event?.room_location || "";
  }

  get showRoomLocationNote() {
    return Boolean(
      this.roomLocation &&
        this.roomLocation !== this.venueName &&
        this.roomLocation !== this.venueAddress
    );
  }

  get roomUrl() {
    return this.event?.room?.id ? `/calendar/rooms/${this.event.room.id}` : "";
  }

  get hasRoomUrl() {
    return Boolean(this.roomUrl);
  }

  get hasRoom() {
    return Boolean(this.roomName || this.roomLocation);
  }

  get hasLocations() {
    return this.locations.length > 0;
  }

  get editLocationOptions() {
    return this.locations.map((location) => ({
      ...location,
      value: locationValue(location),
      isSelected: locationValue(location) === this.editSelectedLocationId,
    }));
  }

  get hasNoEditLocationSelected() {
    return !this.editSelectedLocationId;
  }

  selectedEditLocationRecord() {
    return this.locations.find((location) =>
      [locationValue(location), location.slug, location.name, location.address]
        .filter(Boolean)
        .includes(this.editSelectedLocationId)
    );
  }

  selectedEditLocationPublicAddress(location = this.selectedEditLocationRecord()) {
    return location?.address || location?.name || "";
  }

  findLocationForValue(value) {
    if (!value) {
      return null;
    }

    return this.locations.find((location) =>
      [locationValue(location), location.slug, location.name, location.address]
        .filter(Boolean)
        .includes(value)
    );
  }

  selectedLocationForRoom(room) {
    return this.findLocationForValue(room?.location);
  }

  editRoomMatchesSelectedLocation(room) {
    if (!this.editSelectedLocationId) {
      return true;
    }

    const selectedLocation = this.selectedEditLocationRecord();
    const aliases = [
      selectedLocation?.name,
      selectedLocation?.address,
      selectedLocation?.slug,
      locationValue(selectedLocation),
    ].filter(Boolean);

    return aliases.includes(room.location);
  }

  get editRoomOptions() {
    const selectedRoomId = this.editRoomId;

    return this.rooms
      .filter((room) => this.editRoomMatchesSelectedLocation(room))
      .map((room) => ({
        ...room,
        isSelected: `${room.id}` === `${selectedRoomId}`,
      }));
  }

  get roomOptions() {
    const selectedRoomId = this.editRoomId;

    return this.rooms.map((room) => ({
      ...room,
      isSelected: `${room.id}` === `${selectedRoomId}`,
    }));
  }

  get categoryOptions() {
    const selectedCategoryId = this.editCategoryId;

    return this.categories.map((category) => ({
      ...category,
      isSelected: `${category.id}` === `${selectedCategoryId}`,
    }));
  }

  get hasNoEditRoomSelected() {
    return !this.editRoomId;
  }

  get hasNoEditCategorySelected() {
    return !this.editCategoryId;
  }

  get editEndDate() {
    return this.editEndsNextDay ? nextDayString(this.editDate) : this.editDate;
  }

  get editEndDateLabel() {
    return eventDate(this.editEndDate);
  }

  get locationUrl() {
    return this.event?.location_record ? locationUrl(this.event.location_record) : "";
  }

  get hasLocationUrl() {
    return Boolean(this.locationUrl);
  }

  get roomLocationUrl() {
    return this.event?.room?.location_record ? locationUrl(this.event.room.location_record) : "";
  }

  get hasRoomLocationUrl() {
    return Boolean(this.roomLocationUrl);
  }

  get hasTopic() {
    return Boolean(this.event?.topic_id);
  }

  get eventIsPublished() {
    return this.event?.status === "published";
  }

  get eventIsDraft() {
    return this.event?.status === "draft";
  }

  get eventIsPendingApproval() {
    return this.event?.status === "pending_approval";
  }

  get eventIsRejected() {
    return this.event?.status === "rejected";
  }

  get canCreateTopic() {
    return this.canEdit && this.eventIsPublished && !this.hasTopic && !this.hasChat;
  }

  get topicUrl() {
    return this.hasTopic ? `/t/-/${this.event.topic_id}` : "";
  }

  get chatUrl() {
    return this.event?.chat_url || "";
  }

  get hasChat() {
    return Boolean(this.chatUrl || this.event?.has_chat === true);
  }

  get canCreateChat() {
    return this.event?.can_create_chat === true && this.eventIsPublished && !this.hasTopic && !this.chatUrl;
  }

  get chatTranscriptCountLabel() {
    const count = this.chatItems.length;

    if (count === 1) {
      return "1 visible message";
    }

    return `${count} visible messages`;
  }

  get chatTranscriptSummary() {
    if (this.chatLoadError) {
      return "Open the linked chat thread in Discourse Chat.";
    }

    if (this.hasChatTranscript) {
      return `${this.chatTranscriptCountLabel} from the linked chat thread.`;
    }

    return "No chat messages are visible in this thread yet.";
  }

  get chatChannelTitle() {
    return this.chatChannel?.title || "linked chat channel";
  }

  get hasChatChannelControls() {
    return Boolean(this.currentUser && this.chatChannel?.id && this.chatChannel?.canJoin);
  }

  get isFollowingChatChannel() {
    return this.chatChannel?.following === true;
  }

  get chatChannelMembershipActionLabel() {
    if (this.isUpdatingChatChannelMembership) {
      return this.isFollowingChatChannel ? "Unfollowing..." : "Following...";
    }

    return this.isFollowingChatChannel ? "Unfollow channel" : "Follow channel";
  }

  get chatChannelMembershipHint() {
    if (this.isFollowingChatChannel) {
      return `You are following ${this.chatChannelTitle} for notifications where Discourse Chat supports them.`;
    }

    return `Follow ${this.chatChannelTitle} for notifications where Discourse Chat supports them.`;
  }

  get hasChatTranscript() {
    return this.chatItems.length > 0;
  }

  get chatTranscriptHasMore() {
    return this.chatCanLoadMorePast || this.chatCanLoadMoreFuture;
  }

  get chatItems() {
    return (this.chatMessages || []).map((message) => {
      const username = message.user?.username || "";
      const displayName = message.user?.name || username || "Community member";

      return {
        ...message,
        body: chatMessageBody(message),
        cookedHtml: htmlSafe(message.deletedAt ? "" : message.cooked || ""),
        hasCooked: Boolean(!message.deletedAt && message.cooked),
        displayName,
        userUrl: userUrl(username),
        timeLabel: eventDateTime(message.createdAt),
        className: [
          "discourse-events-calendar__chat-message",
          message.deletedAt ? "discourse-events-calendar__chat-message--deleted" : null,
        ]
          .filter(Boolean)
          .join(" "),
      };
    });
  }

  get hasDiscussionPosts() {
    return this.discussionItems.length > 0;
  }

  get discussionCountLabel() {
    const replies = this.discussionTopic?.replyCount || 0;
    const posts = this.discussionTopic?.postsCount || 0;

    if (replies === 1) {
      return "1 reply";
    }

    if (replies > 1) {
      return `${replies} replies`;
    }

    if (posts === 1) {
      return "1 post";
    }

    return `${posts} posts`;
  }

  get discussionItems() {
    return (this.discussionPosts || []).map((post) => ({
      ...post,
      displayName: post.name || post.username || "Community member",
      userUrl: userUrl(post.username),
      postUrl: this.topicUrl && post.postNumber ? `${this.topicUrl}/${post.postNumber}` : this.topicUrl,
      timeLabel: eventDateTime(post.createdAt),
      cookedHtml: htmlSafe(post.cooked || ""),
      user: {
        username: post.username,
        name: post.name,
        avatar_template: post.avatar_template,
      },
    }));
  }

  get discussionRemainingCount() {
    const visibleReplyCount = this.discussionItems.length;
    const replyCount =
      this.discussionTopic?.replyCount ||
      Math.max(0, (this.discussionTopic?.postsCount || 0) - 1);

    return Math.max(0, replyCount - visibleReplyCount);
  }

  get discussionRemainingLabel() {
    if (this.discussionRemainingCount === 1) {
      return "1 more reply is available in the full topic.";
    }

    return `${this.discussionRemainingCount} more replies are available in the full topic.`;
  }

  get canReplyToDiscussion() {
    return Boolean(
      this.hasTopic &&
        this.currentUser &&
        !this.discussionLoadError &&
        this.discussionTopic?.canCreatePost !== false
    );
  }

  get canSubmitDiscussionReply() {
    return (
      this.canReplyToDiscussion &&
      !this.isSavingDiscussionReply &&
      this.discussionReply.trim().length > 0
    );
  }

  get cannotSubmitDiscussionReply() {
    return !this.canSubmitDiscussionReply;
  }

  get icsUrl() {
    return this.event?.id ? `/calendar/events/${this.event.id}.ics` : "";
  }

  get attendeesCsvUrl() {
    return this.event?.id ? `/calendar/events/${this.event.id}/attendees.csv` : "";
  }

  get attendeeStatusLabel() {
    return statusLabel(this.currentUserRsvpStatus) || "No RSVP yet";
  }

  get canRsvp() {
    return this.event?.can_rsvp === true;
  }

  get isExternalRsvp() {
    return this.event?.rsvp_mode === "external" && Boolean(this.externalRsvpUrl);
  }

  get externalRsvpUrl() {
    return safeHttpUrl(this.event?.external_rsvp_url || this.event?.external_url);
  }

  get externalRsvpButtonLabel() {
    return "RSVP";
  }

  get showNativeRsvpDetails() {
    return !this.isExternalRsvp;
  }

  get showRsvpPanel() {
    return (
      this.isExternalRsvp ||
      this.event?.rsvp_ui_enabled === true ||
      this.event?.can_rsvp === true
    );
  }

  get canExportAttendees() {
    return !this.isExternalRsvp && this.event?.can_export_attendees === true;
  }

  get attendeeVisibilityLabel() {
    return this.canViewAllAttendeeStatuses
      ? "All RSVP statuses are visible to you."
      : "Showing public going and interested RSVPs.";
  }

  get goingCount() {
    return this.activeRsvpCounts.going || 0;
  }

  get interestedCount() {
    return this.activeRsvpCounts.interested || 0;
  }

  get waitlistedCount() {
    return this.activeRsvpCounts.waitlisted || 0;
  }

  get rsvpSpotsLabel() {
    if (!this.hasCapacity) {
      return "";
    }

    const remaining = Math.max(0, this.event.capacity - this.goingCount);
    return remaining === 0 ? "At capacity" : `${remaining} spots left`;
  }

  get rsvpCapacityLabel() {
    if (!this.event?.rsvp_enabled) {
      return "RSVP is disabled.";
    }

    if (this.hasCapacity) {
      const remaining = Math.max(0, this.event.capacity - this.goingCount);
      const status = remaining === 0 ? "At capacity" : `${remaining} spots left`;
      const waitlist = this.waitlistedCount > 0 ? ` · ${this.waitlistedCount} waitlisted` : "";

      return `${this.goingCount}/${this.event.capacity} going · ${status}${waitlist}`;
    }

    const interested = this.interestedCount > 0 ? ` · ${this.interestedCount} interested` : "";
    const waitlist = this.waitlistedCount > 0 ? ` · ${this.waitlistedCount} waitlisted` : "";

    return `${this.goingCount} going${interested}${waitlist}`;
  }

  get canEdit() {
    return this.event?.can_edit === true;
  }

  get canCancel() {
    return this.event?.can_cancel === true;
  }

  get canUncancel() {
    return this.event?.can_uncancel === true && this.event?.status === "canceled";
  }

  get canReviewEvent() {
    return this.event?.can_review === true && this.eventIsPendingApproval;
  }

  get cannotReviewEvent() {
    return this.isApprovingEvent || this.isRejectingEvent || this.isSavingEvent;
  }

  get canPublish() {
    return this.canEdit && this.event?.status === "draft";
  }

  get canRevertToDraft() {
    return this.canEdit && this.event?.status === "published";
  }

  get cannotUpdateStatus() {
    return this.isUpdatingStatus || this.isSavingEvent;
  }

  get hasCapacity() {
    return Boolean(this.event?.capacity);
  }

  get hasTimezone() {
    return Boolean(this.event?.timezone);
  }

  get visibilityLabel() {
    return visibilityLabel(this.event?.visibility, this.event?.hidden);
  }

  get categoryName() {
    return this.event?.category?.name || "";
  }

  get hasCategory() {
    return Boolean(this.categoryName);
  }

  get tagNames() {
    return (this.event?.tags || []).map((tag) => tag.name).join(", ");
  }

  get hasTags() {
    return Boolean(this.tagNames);
  }

  get recurrenceFact() {
    // The serializer emits external_rrule only for a multi-weekday BYDAY rule
    // (as a full FREQ=WEEKLY;...;BYDAY=... string), so its presence alone marks
    // the "custom schedule" case.
    if (this.event?.external_rrule) {
      return "Repeats on a custom schedule";
    }

    const rule = this.event?.recurrence_rule || "none";

    if (rule === "none") {
      return "Does not repeat";
    }

    const label = recurrenceLabel(rule).toLowerCase();
    const interval = normalizedInterval(this.event?.recurrence_interval || 1);
    const cadence =
      interval > 1 ? `every ${interval} ${recurrenceUnit(rule, interval)}` : label;
    const until = eventDate(this.event?.recurrence_until);

    return until ? `Repeats ${cadence} until ${until}` : `Repeats ${cadence}`;
  }

  get eventRepeats() {
    return Boolean(this.event?.recurrence_rule && this.event.recurrence_rule !== "none");
  }

  get hasManagementActions() {
    return this.canEdit || this.canCancel || this.canUncancel || this.canReviewEvent;
  }

  // Desktop overflow kebab on the detail action row: holds the secondary /
  // destructive actions that don't warrant a permanent inline button. The
  // inline row keeps Add to calendar, Discussion, Chat, Edit, and Approve;
  // everything else (Create discussion/chat, Reject, Publish, Revert to draft,
  // Cancel, Uncancel) lives behind the kebab. Reject rides along with
  // canReviewEvent since Approve stays inline.
  get hasDetailOverflowActions() {
    return (
      this.canCreateTopic ||
      this.canCreateChat ||
      this.canPublish ||
      this.canRevertToDraft ||
      this.canCancel ||
      this.canUncancel ||
      this.canReviewEvent
    );
  }

  get hasValidEditTimeRange() {
    if (this.editAllDay) {
      return Boolean(this.editDate);
    }

    if (!this.editDate || !this.editStartTime || !this.editEndTime) {
      return false;
    }

    return (
      dateWithTime(this.editEndDate, this.editEndTime) >
      dateWithTime(this.editDate, this.editStartTime)
    );
  }

  get hasInvalidEditTimeRange() {
    if (this.editAllDay) {
      return false;
    }

    return !this.hasValidEditTimeRange;
  }

  get editRecurrenceOptions() {
    return ["none", "daily", "weekly", "monthly", "yearly"].map((rule) => ({
      rule,
      label: recurrenceLabel(rule),
      isSelected: this.editRecurrenceRule === rule,
    }));
  }

  get editTimeOptions() {
    const options = [];

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const value = `${pad(hour)}:${pad(minute)}`;
        options.push({ value, label: timeLabel(value) });
      }
    }

    return options;
  }

  get editStartTimeOptions() {
    return this.editTimeOptions.map((option) => ({
      ...option,
      isSelected: option.value === this.editStartTime,
    }));
  }

  get editEndTimeOptions() {
    return this.editTimeOptions.map((option) => ({
      ...option,
      isSelected: option.value === this.editEndTime,
    }));
  }

  get editTimezoneOptions() {
    return timezoneOptions(this.editTimezone || browserTimeZone());
  }

  get isEditRecurring() {
    return this.editRecurrenceRule !== "none";
  }

  get isEditWeeklyRecurrence() {
    return this.editRecurrenceRule === "weekly";
  }

  get editRecurrenceIntervalLabel() {
    const interval = normalizedInterval(this.editRecurrenceInterval);
    return `Every ${interval} ${recurrenceUnit(this.editRecurrenceRule, interval)}`;
  }

  get selectedEditRecurrenceByday() {
    return this.editRecurrenceByday
      .split(",")
      .map((day) => day.trim())
      .filter(Boolean);
  }

  get editRecurrenceWeekdayOptions() {
    const selected = new Set(this.selectedEditRecurrenceByday);

    return RECURRENCE_WEEKDAYS.map((weekday) => ({
      ...weekday,
      isSelected: selected.has(weekday.code),
    }));
  }

  get editCustomWeeklyRrule() {
    if (!this.isEditWeeklyRecurrence) {
      return "";
    }

    const byday = this.selectedEditRecurrenceByday;
    if (byday.length === 0) {
      return "";
    }

    const defaultDay = weekdayCodeForDate(this.editDate);
    if (byday.length === 1 && byday[0] === defaultDay) {
      return "";
    }

    return weeklyRrule({
      byday,
      interval: this.editRecurrenceInterval,
      until: this.editRecurrenceUntil,
    });
  }

  get editRecurrenceUntilMin() {
    return this.editDate;
  }

  get hasValidEditRecurrenceRange() {
    return (
      !this.isEditRecurring ||
      (this.editRecurrenceUntil && !dateIsBefore(this.editRecurrenceUntil, this.editDate))
    );
  }

  get hasInvalidEditRecurrenceRange() {
    return !this.hasValidEditRecurrenceRange;
  }

  get canSaveEdit() {
    return (
      this.canEdit &&
      !this.isSavingEvent &&
      this.editTitle.trim().length > 0 &&
      this.hasValidEditTimeRange &&
      this.hasValidEditRecurrenceRange &&
      this.hasValidEditRsvpDetails
    );
  }

  get cannotSaveEdit() {
    return !this.canSaveEdit;
  }

  get hasValidEditRsvpDetails() {
    return (
      !this.editRsvpEnabled ||
      this.editRsvpMode !== "external" ||
      normalizedUrlInput(this.editExternalRsvpUrl).length > 0
    );
  }

  get isEditNativeRsvpMode() {
    return this.editRsvpMode !== "external";
  }

  get isEditExternalRsvpMode() {
    return this.editRsvpMode === "external";
  }

  get rsvpCounts() {
    const counts = this.activeRsvpCounts;

    return RSVP_STATUSES.map((item) => ({
      ...item,
      count: counts[item.status] || 0,
    }));
  }

  get rsvpOptions() {
    const statuses =
      this.event?.rsvp_allow_interested === false
        ? RSVP_ACTION_STATUSES.filter((item) => item.status !== "interested")
        : RSVP_ACTION_STATUSES;

    return statuses.map((item) => {
      const isSelected = this.currentUserRsvpStatus === item.status;
      const classNames = ["btn", "btn-small", "discourse-events-calendar__rsvp-button"];

      if (isSelected) {
        classNames.push("btn-primary");
      }

      return {
        ...item,
        isSelected,
        isDisabled: this.isSavingRsvp,
        className: classNames.join(" "),
      };
    });
  }

  get currentUserRsvpStatus() {
    if (this.selectedRsvpOccurrence) {
      return this.selectedRsvpOccurrence.current_user_rsvp_status || "";
    }

    if (
      this.event &&
      Object.prototype.hasOwnProperty.call(this.event, "series_current_user_rsvp_status")
    ) {
      return this.event.series_current_user_rsvp_status || "";
    }

    return this.event?.current_user_rsvp_status || this.event?.attendee_status || "";
  }

  get activeRsvpCounts() {
    if (this.selectedRsvpOccurrence) {
      return this.selectedRsvpOccurrence.rsvp_counts || {};
    }

    return this.event?.series_rsvp_counts || this.event?.rsvp_counts || {};
  }

  get rsvpOccurrences() {
    return Array.isArray(this.event?.rsvp_occurrences) ? this.event.rsvp_occurrences : [];
  }

  get selectedRsvpOccurrence() {
    if (this.selectedRsvpOccurrenceKey === "series") {
      return null;
    }

    return this.rsvpOccurrences.find(
      (occurrence) => occurrence.occurrence_key === this.selectedRsvpOccurrenceKey
    );
  }

  get hasRsvpOccurrencePicker() {
    return this.eventRepeats && this.rsvpOccurrences.length > 0;
  }

  // Default an RSVP to a single occurrence (the next one) rather than the whole
  // series, which is the less common intent. The series stays available via the
  // occurrence disclosure.
  get defaultRsvpOccurrenceKey() {
    const occurrences = this.rsvpOccurrences;
    return occurrences.length > 0 ? occurrences[0].occurrence_key : "series";
  }

  get selectedRsvpOccurrenceLabel() {
    const selected = this.rsvpOccurrenceOptions.find((option) => option.isSelected);
    return selected ? selected.label : "This event";
  }

  get isSeriesRsvpSelected() {
    return this.selectedRsvpOccurrenceKey === "series";
  }

  get isGoingRsvp() {
    return this.currentUserRsvpStatus === "going";
  }

  get isInterestedRsvp() {
    return this.currentUserRsvpStatus === "interested";
  }

  get showInterestedAction() {
    return this.event?.rsvp_allow_interested !== false;
  }

  // Compact, date-only label (no time — events recur at most daily and the time
  // already appears in the event facts above).
  get rsvpAppliesToLabel() {
    if (this.isSeriesRsvpSelected) {
      return "the whole series";
    }

    const occurrence = this.selectedRsvpOccurrence;
    const startsAt = occurrence?.starts_at || this.event?.starts_at;
    return eventDatePart(startsAt, { month: "short", day: "numeric" }, "this event");
  }

  get rsvpOccurrenceOptions() {
    const seriesLabel = this.eventRepeats ? "Whole series" : "This event";
    const rows = [
      {
        value: "series",
        label: seriesLabel,
        isSelected: this.selectedRsvpOccurrenceKey === "series",
      },
    ];

    this.rsvpOccurrences.forEach((occurrence) => {
      const date = eventDate(occurrence.starts_at);
      const time = eventTimeRange(occurrence.starts_at, occurrence.ends_at, occurrence.all_day);
      rows.push({
        value: occurrence.occurrence_key,
        label: [date, time].filter(Boolean).join(" - "),
        isSelected: this.selectedRsvpOccurrenceKey === occurrence.occurrence_key,
      });
    });

    return rows;
  }

  get attendees() {
    return (this.attendeeList || []).map((attendee) => {
      const label = statusLabel(attendee.status);
      const titleName =
        attendee.name && attendee.username
          ? `${attendee.name} (@${attendee.username})`
          : attendee.name || attendee.username || "Community member";

      return {
        ...attendee,
        label,
        userUrl: userUrl(attendee.username),
        displayName: attendee.name || attendee.username,
        chipName: attendeeChipName(attendee),
        chipClass: [
          "discourse-events-calendar__attendee-chip",
          `discourse-events-calendar__attendee-chip--${attendee.status.replace("_", "-")}`,
        ].join(" "),
        title: `${titleName} · ${label}`,
      };
    });
  }

  get hasAttendees() {
    return this.attendees.length > 0;
  }

  get attendeeGroups() {
    return RSVP_STATUSES.map((item) => ({
      ...item,
      attendees: this.attendees.filter((attendee) => attendee.status === item.status),
    }))
      .filter((group) => group.attendees.length > 0)
      .map((group) => ({
        ...group,
        countLabel: `${group.attendees.length}`,
      }));
  }

  get attendeeFacepileCandidates() {
    return this.attendees
      .filter((attendee) => RSVP_FACEPILE_STATUSES.includes(attendee.status))
      .sort((left, right) => {
        const leftRank = RSVP_FACEPILE_STATUSES.indexOf(left.status);
        const rightRank = RSVP_FACEPILE_STATUSES.indexOf(right.status);

        if (leftRank !== rightRank) {
          return leftRank - rightRank;
        }

        return (left.displayName || "").localeCompare(right.displayName || "");
      });
  }

  get attendeeFacepile() {
    return this.attendeeFacepileCandidates.slice(0, RSVP_FACEPILE_LIMIT).map((attendee) => ({
      ...attendee,
      className: [
        "discourse-events-calendar__attendee-face",
        `discourse-events-calendar__attendee-face--${attendee.status.replace("_", "-")}`,
      ].join(" "),
    }));
  }

  get hasAttendeeFacepile() {
    return this.attendeeFacepile.length > 0;
  }

  // Authoritative totals from rsvp_counts (the loaded attendee list can be
  // shorter than the real count, which previously showed "11 going" next to a
  // facepile summarised as "10 going").
  get facepileTotalCount() {
    return this.goingCount + this.interestedCount + this.waitlistedCount;
  }

  get attendeeFacepileOverflowCount() {
    return Math.max(0, this.facepileTotalCount - this.attendeeFacepile.length);
  }

  get attendeeFacepileSummary() {
    const parts = [
      this.goingCount > 0 ? `${this.goingCount} going` : null,
      this.interestedCount > 0 ? `${this.interestedCount} interested` : null,
      this.waitlistedCount > 0 ? `${this.waitlistedCount} waitlisted` : null,
    ].filter(Boolean);

    return parts.join(" · ");
  }

  resetEditFields() {
    if (!this.event) {
      return;
    }

    this.editTitle = this.event.title || "";
    this.editImageUpload = uploadFromResource(this.event) || null;
    this.editImageUploadId = this.event.image_upload_id ? `${this.event.image_upload_id}` : "";
    this.editImageUploadError = "";
    this.isUploadingEditImage = false;
    this.showEditAiImagePrompt = false;
    this.editAiImagePrompt = "";
    this.pendingEditAiImageUpload = null;
    this.isComposingEditAiImagePrompt = false;
    this.isGeneratingEditAiImage = false;
    this.editDate = eventDateInput(this.event.starts_at);
    this.editStartTime = eventTimeInput(this.event.starts_at);
    this.editEndTime = eventTimeInput(this.event.ends_at || this.event.starts_at);
    this.editAllDay = this.event.all_day === true;
    this.editEndsNextDay = Boolean(
      this.event.ends_at &&
        this.event.starts_at &&
        eventDateInput(this.event.ends_at) !== eventDateInput(this.event.starts_at)
    );
    this.editLocation = this.event.location || "";
    this.editPlaceId = this.event.place_id ? `${this.event.place_id}` : "";
    this.editPlaceQuery = this.event.place?.name || "";
    this.editPlaceOptions = [];
    this.isLoadingEditPlaces = false;
    const selectedLocation =
      this.findLocationForValue(this.event.location_id) ||
      this.findLocationForValue(this.event.location_record?.id) ||
      this.findLocationForValue(this.editLocation) ||
      this.findLocationForValue(this.event.room?.location_id) ||
      this.findLocationForValue(this.event.room?.location || this.event.room_location);
    this.editSelectedLocationId = selectedLocation ? locationValue(selectedLocation) : "";
    this.editRoomId = this.event.room?.id ? `${this.event.room.id}` : "";
    this.editRoomLocationApplied = this.editLocation;
    this.editCategoryId = this.event.category_id ? `${this.event.category_id}` : "";
    this.editTagNamesCsv = (this.event.tags || []).map((tag) => tag.name).join(", ");
    const roles = Array.isArray(this.event.event_roles) ? this.event.event_roles : [];
    this.editHostNames = participantNamesForRoles(
      roles.filter((role) => role.role === "host")
    );
    if (!this.editHostNames) {
      this.editHostNames = ownerParticipantName(this.event);
    }
    this.editCoHostNames = participantNamesForRoles(
      roles.filter((role) => role.role === "co_host")
    );
    this.editSpeakerNames = participantNamesForRoles(
      roles.filter((role) => role.role === "speaker")
    );
    this.editWithNames = participantNamesForRoles(
      roles.filter((role) => role.role === "custom_host")
    );
    this.showEditPeopleDetails = Boolean(
      this.editCoHostNames || this.editSpeakerNames || this.editWithNames
    );
    this.editRoleUserOptions = [];
    this.isLoadingEditRoleUsers = false;
    this.editDescription = this.event.description || "";
    this.editTimezone = normalizeTimeZone(this.event.timezone, browserTimeZone());
    this.editCapacity = this.event.capacity ? `${this.event.capacity}` : "";
    this.editVisibility = visibilityModeFromFields(this.event.visibility, this.event.hidden);
    const externalWeeklyRrule = parseWeeklyRrule(this.event.external_rrule);
    this.editHadSupportedExternalRrule = Boolean(externalWeeklyRrule);
    this.editRecurrenceRule = externalWeeklyRrule ? "weekly" : this.event.recurrence_rule || "none";
    this.editRecurrenceInterval = `${normalizedInterval(
      externalWeeklyRrule?.interval || this.event.recurrence_interval || 1
    )}`;
    this.editRecurrenceUntil =
      externalWeeklyRrule?.until || eventDateInput(this.event.recurrence_until);
    this.editRecurrenceByday =
      externalWeeklyRrule?.byday?.join(",") ||
      (this.editRecurrenceRule === "weekly" ? weekdayCodeForDate(this.editDate) : "");
    if (this.isEditRecurring && !this.editRecurrenceUntil) {
      this.editRecurrenceUntil = defaultRecurrenceUntil(this.editDate);
    }
    this.editRsvpEnabled = this.event.rsvp_enabled !== false;
    this.editRsvpMode = this.event.rsvp_mode === "external" ? "external" : "native";
    this.editExternalRsvpUrl = this.event.external_rsvp_url || "";
    this.editRsvpAllowInterested = this.event.rsvp_allow_interested !== false;
  }

  @action
  startEditing() {
    this.resetEditFields();
    this.editError = "";
    this.editNotice = "";
    this.isEditing = true;
  }

  @action
  cancelEditing() {
    this.isEditing = false;
    this.editError = "";
    this.teardownEditDescriptionUpload();
  }

  @action
  updateEditDescription(valueOrEvent) {
    this.editDescription = editorValue(valueOrEvent);
  }

  @action
  toggleEditPeopleDetails(event) {
    event?.preventDefault();
    this.showEditPeopleDetails = !this.showEditPeopleDetails;
  }

  @action
  updateEditHostNames(event) {
    this.editHostNames = event.target.value;
    debounce(this, this.loadEditRoleUserOptions, this.editHostNames, 250);
  }

  @action
  updateEditCoHostNames(event) {
    this.editCoHostNames = event.target.value;
    debounce(this, this.loadEditRoleUserOptions, this.editCoHostNames, 250);
  }

  @action
  updateEditSpeakerNames(event) {
    this.editSpeakerNames = event.target.value;
    debounce(this, this.loadEditRoleUserOptions, this.editSpeakerNames, 250);
  }

  @action
  updateEditWithNames(event) {
    this.editWithNames = event.target.value;
    debounce(this, this.loadEditRoleUserOptions, this.editWithNames, 250);
  }

  editEventRoles() {
    return [
      ...participantRoleRows(this.editHostNames, "host", 0),
      ...participantRoleRows(this.editCoHostNames, "co_host", 100),
      ...participantRoleRows(this.editSpeakerNames, "speaker", 200),
      ...participantRoleRows(this.editWithNames, "custom_host", 300),
    ];
  }

  @action
  async uploadEditImage(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    this.editImageUploadError = "";
    this.isUploadingEditImage = true;

    try {
      const upload = await uploadImageFile(file);
      this.editImageUpload = upload;
      this.editImageUploadId = upload?.id ? `${upload.id}` : "";
      this.pendingEditAiImageUpload = null;
    } catch (error) {
      this.editImageUploadError = extractErrorMessage(error, "Image could not be uploaded.");
      popupAjaxError(error);
    } finally {
      this.isUploadingEditImage = false;
    }
  }

  @action
  removeEditImage() {
    this.editImageUpload = null;
    this.editImageUploadId = "";
    this.editImageUploadError = "";
    this.pendingEditAiImageUpload = null;
  }

  @action
  async openEditAiImagePrompt(event) {
    event?.preventDefault?.();

    if (this.cannotComposeEditAiImagePrompt) {
      return;
    }

    if (this.editAiImagePrompt.trim()) {
      this.showEditAiImagePrompt = true;
      this.editImageUploadError = "";
      return;
    }

    await this.composeEditAiImagePrompt();
  }

  @action
  updateEditAiImagePrompt(event) {
    this.editAiImagePrompt = event.target.value;
  }

  @action
  async resetEditAiImagePrompt(event) {
    event?.preventDefault?.();

    this.editAiImagePrompt = "";
    await this.composeEditAiImagePrompt();
  }

  @action
  async regenerateEditAiImage(event) {
    event?.preventDefault?.();

    this.pendingEditAiImageUpload = null;

    if (!this.editAiImagePrompt.trim()) {
      await this.composeEditAiImagePrompt();
    }

    if (!this.editAiImagePrompt.trim()) {
      return;
    }

    await this.generateEditAiImage(event);
  }

  @action
  acceptEditAiImage(event) {
    event?.preventDefault?.();

    if (!this.pendingEditAiImageUpload) {
      return;
    }

    this.editImageUpload = this.pendingEditAiImageUpload;
    this.editImageUploadId = this.editImageUpload?.id ? `${this.editImageUpload.id}` : "";
    this.pendingEditAiImageUpload = null;
    this.editImageUploadError = "";
    this.showEditAiImagePrompt = false;
  }

  @action
  discardEditAiImage(event) {
    event?.preventDefault?.();

    this.pendingEditAiImageUpload = null;
    this.editImageUploadError = "";
  }

  @action
  async generateEditAiImage(event) {
    event?.preventDefault?.();

    if (this.cannotGenerateEditAiImage) {
      return;
    }

    this.isGeneratingEditAiImage = true;
    this.editImageUploadError = "";

    try {
      const response = await ajax("/calendar/events/generate_image.json", {
        type: "POST",
        data: {
          prompt: this.editAiImagePrompt,
          event: this.editAiImagePromptEventData(),
        },
      });
      const upload = response?.upload;
      this.pendingEditAiImageUpload = upload || null;
      this.showEditAiImagePrompt = false;
    } catch (error) {
      this.editImageUploadError = extractErrorMessage(error, "AI image could not be generated.");
      popupAjaxError(error);
    } finally {
      this.isGeneratingEditAiImage = false;
    }
  }

  async composeEditAiImagePrompt() {
    this.isComposingEditAiImagePrompt = true;
    this.editImageUploadError = "";

    try {
      const response = await ajax("/calendar/events/image_prompt.json", {
        type: "POST",
        data: {
          event: this.editAiImagePromptEventData(),
        },
      });

      this.editAiImagePrompt = response?.prompt || "";
      this.showEditAiImagePrompt = this.editAiImagePrompt.trim().length > 0;
    } catch (error) {
      this.editImageUploadError = extractErrorMessage(
        error,
        "AI image prompt could not be prepared."
      );
    } finally {
      this.isComposingEditAiImagePrompt = false;
    }
  }

  editAiImagePromptEventData() {
    const room = this.rooms.find((candidate) => `${candidate.id}` === `${this.editRoomId}`);

    return {
      title: this.editTitle.trim(),
      description: this.editDescription.trim(),
      starts_at: this.editAllDay ? `${this.editDate}T00:00` : `${this.editDate} ${this.editStartTime}`,
      ends_at: this.editAllDay
        ? `${nextDayString(this.editDate)}T00:00`
        : `${this.editEndDate} ${this.editEndTime}`,
      timezone: normalizeTimeZone(this.editTimezone, browserTimeZone()),
      location: this.editLocation.trim(),
      category_id: this.editCategoryId,
      room_id: this.editRoomId,
      room_name: room?.name || "",
      place_id: this.editPlaceId,
      place_name: this.editPlaceQuery,
      tag_names: this.editTagNamesCsv,
      event_roles: this.editEventRoles(),
    };
  }

  @action
  updateDiscussionReply(valueOrEvent) {
    this.discussionReply = editorValue(valueOrEvent);
    this.discussionReplyError = "";
    this.discussionReplyNotice = "";
  }

  ensureEditDescriptionUpload() {
    if (!this.editDescriptionUpload) {
      this.editDescriptionUpload = new UppyComposerUpload(getOwner(this), {
        composerEventPrefix: "discourse-events-calendar:event-edit-description",
        composerModel: { privateMessage: false },
        uploadMarkdownResolvers: [],
        uploadPreProcessors: [],
        uploadHandlers: [],
        fileUploadElementId: this.editDescriptionFileUploadElementId,
      });
      this.editDescriptionUpload.uppyId = "event-edit-description-uppy";
    }

    return this.editDescriptionUpload;
  }

  setupEditDescriptionUploadIfReady() {
    if (this.editDescriptionUploadSetup || !this.editDescriptionUploadElement) {
      return;
    }

    if (!document.getElementById(this.editDescriptionFileUploadElementId)) {
      return;
    }

    const upload = this.ensureEditDescriptionUpload();

    if (!upload.textManipulation) {
      return;
    }

    upload.setup(this.editDescriptionUploadElement);
    this.editDescriptionUploadSetup = true;
  }

  @action
  setupEditDescriptionEditor(textManipulation) {
    const upload = this.ensureEditDescriptionUpload();
    upload.textManipulation = textManipulation;
    this.setupEditDescriptionUploadIfReady();
  }

  @action
  setupEditDescriptionUpload(element) {
    this.editDescriptionUploadElement = element;
    this.setupEditDescriptionUploadIfReady();
  }

  @action
  registerEditDescriptionFileInput() {
    this.setupEditDescriptionUploadIfReady();
  }

  @action
  teardownEditDescriptionUpload() {
    if (this.editDescriptionUploadSetup && this.editDescriptionUpload) {
      this.editDescriptionUpload.teardown();
    }

    this.editDescriptionUpload = null;
    this.editDescriptionUploadElement = null;
    this.editDescriptionUploadSetup = false;
  }

  @action
  editDescriptionExtraButtons(toolbar) {
    if (!this.currentUser || !this.site?.desktopView) {
      return;
    }

    toolbar.addButton({
      id: "upload",
      group: "insertions",
      icon: this.editDescriptionUploadIcon,
      title: "upload",
      sendAction: this.openEditDescriptionFilePicker,
    });
  }

  @action
  openEditDescriptionFilePicker() {
    document.getElementById(this.editDescriptionFileUploadElementId)?.click();
  }

  @action
  updateEditDate(event) {
    this.editDate = event.target.value;

    if (
      this.isEditRecurring &&
      (!this.editRecurrenceUntil || dateIsBefore(this.editRecurrenceUntil, this.editDate))
    ) {
      this.editRecurrenceUntil = defaultRecurrenceUntil(this.editDate);
    }

    if (this.isEditWeeklyRecurrence && !this.editRecurrenceByday) {
      this.ensureEditWeeklyBydaySelection();
    }
  }

  @action
  updateEditStartTime(event) {
    this.editStartTime = event.target.value;
  }

  @action
  updateEditEndTime(event) {
    this.editEndTime = event.target.value;
  }

  @action
  updateEditTimezone(event) {
    this.editTimezone = normalizeTimeZone(event.target.value, browserTimeZone());
  }

  @action
  toggleEditAllDay(event) {
    this.editAllDay = event.target.checked;

    if (!this.editAllDay && this.editStartTime === this.editEndTime) {
      this.editStartTime = "09:00";
      this.editEndTime = "10:00";
      this.editEndsNextDay = false;
    }
  }

  @action
  async renderPlaceMap(element) {
    this.placeMapElement = element;
    this.placeMap?.remove?.();
    this.placeMap = null;

    if (!this.hasPlaceMap || typeof window === "undefined") {
      return;
    }

    const requireModule = window.requirejs || window.require;
    if (typeof requireModule !== "function") {
      return;
    }

    let mapModule;
    try {
      mapModule = requireModule("discourse/plugins/discourse-places/discourse/lib/place-map");
    } catch {
      return;
    }

    if (typeof mapModule?.renderPlaceMap !== "function") {
      return;
    }

    let map;
    try {
      map = await mapModule.renderPlaceMap(element, {
        markers: this.placeMapMarkers,
        provider: this.siteSettings?.discourse_places_map_provider,
        maptilerApiKey: this.siteSettings?.discourse_places_maptiler_api_key,
        maptilerStyle: this.siteSettings?.discourse_places_maptiler_style,
        openFreeMapStyle: this.siteSettings?.discourse_places_openfreemap_style,
      });
    } catch {
      return;
    }

    if (this.placeMapElement === element) {
      this.placeMap = map;
    } else {
      map?.remove?.();
    }
  }

  @action
  destroyPlaceMap() {
    this.placeMap?.remove?.();
    this.placeMap = null;
    this.placeMapElement = null;
  }

  @action
  updateEditLocationChoice(event) {
    this.editSelectedLocationId = event.target.value;
    const selectedLocation = this.selectedEditLocationRecord();

    if (selectedLocation) {
      this.editLocation = this.selectedEditLocationPublicAddress(selectedLocation);
      this.editRoomLocationApplied = this.editLocation;
    }

    if (this.editRoomId) {
      const selectedRoom = this.rooms.find((room) => `${room.id}` === `${this.editRoomId}`);
      if (selectedRoom && !this.editRoomMatchesSelectedLocation(selectedRoom)) {
        this.editRoomId = "";
        this.editRoomLocationApplied = "";
      }
    }
  }

  @action
  updateEditPlaceQuery(event) {
    this.editPlaceQuery = event.target.value;
    this.editPlaceId = "";
    debounce(this, this.loadEditPlaceOptions, this.editPlaceQuery, 250);
  }

  async loadEditPlaceOptions(query) {
    const value = `${query || ""}`.trim();

    if (!this.placesEnabled || value.length < 2) {
      this.editPlaceOptions = [];
      this.isLoadingEditPlaces = false;
      return;
    }

    const requestId = ++this.editPlaceRequestId;
    this.isLoadingEditPlaces = true;

    try {
      const response = await ajax("/places/autocomplete.json", {
        data: { q: value, limit: 8 },
      });

      if (requestId !== this.editPlaceRequestId) {
        return;
      }

      this.editPlaceOptions = (Array.isArray(response?.places) ? response.places : [])
        .map(normalizePlaceOption)
        .filter(Boolean);
    } catch {
      if (requestId === this.editPlaceRequestId) {
        this.editPlaceOptions = [];
      }
    } finally {
      if (requestId === this.editPlaceRequestId) {
        this.isLoadingEditPlaces = false;
      }
    }
  }

  async loadEditRoleUserOptions(query) {
    const value = roleSearchTerm(query);

    if (value.length < 2) {
      this.editRoleUserOptions = [];
      this.isLoadingEditRoleUsers = false;
      return;
    }

    const requestId = ++this.editRoleUserRequestId;
    this.isLoadingEditRoleUsers = true;

    try {
      const response = await ajax("/u/search/users.json", {
        data: { term: value, limit: 8 },
      });

      if (requestId !== this.editRoleUserRequestId) {
        return;
      }

      this.editRoleUserOptions = (Array.isArray(response?.users) ? response.users : [])
        .map(normalizeUserOption)
        .filter(Boolean);
    } catch {
      if (requestId === this.editRoleUserRequestId) {
        this.editRoleUserOptions = [];
      }
    } finally {
      if (requestId === this.editRoleUserRequestId) {
        this.isLoadingEditRoleUsers = false;
      }
    }
  }

  @action
  selectEditPlace(place, event) {
    event?.preventDefault();
    const selectedPlace = normalizePlaceOption(place);
    if (!selectedPlace) {
      return;
    }

    this.editPlaceId = `${selectedPlace.id}`;
    this.editPlaceQuery = selectedPlace.name;
    this.editPlaceOptions = [];

    const publicLocation = selectedPlace.address || selectedPlace.name;
    if (publicLocation && (!this.editLocation || this.editLocation === this.editRoomLocationApplied)) {
      this.editLocation = publicLocation;
      this.editRoomLocationApplied = publicLocation;
    }
  }

  @action
  clearEditPlace(event) {
    event?.preventDefault();
    this.editPlaceQuery = "";
    this.editPlaceId = "";
    this.editPlaceOptions = [];
    this.isLoadingEditPlaces = false;
  }

  @action
  updateEditPublicLocation(event) {
    this.editLocation = event.target.value;
  }

  @action
  updateEditRoom(event) {
    this.editRoomId = event.target.value;

    const room = this.rooms.find((candidate) => `${candidate.id}` === this.editRoomId);

    if (!room) {
      if (this.editLocation && this.editLocation === this.editRoomLocationApplied) {
        this.editLocation = "";
      }

      this.editRoomLocationApplied = "";
      return;
    }

    const matchingLocation = this.selectedLocationForRoom(room);
    const publicLocation = matchingLocation
      ? this.selectedEditLocationPublicAddress(matchingLocation)
      : room.location;

    if (matchingLocation) {
      this.editSelectedLocationId = locationValue(matchingLocation);
    }

    if (room.location && (!this.editLocation || this.editLocation === this.editRoomLocationApplied)) {
      this.editLocation = publicLocation;
      this.editRoomLocationApplied = publicLocation;
    }
  }

  @action
  updateEditCategory(event) {
    this.editCategoryId = event.target.value;
  }

  @action
  updateEditTagNames(event) {
    this.editTagNamesCsv = event.target.value;
  }

  @action
  toggleEditEndsNextDay(event) {
    this.editEndsNextDay = event.target.checked;
  }

  @action
  updateEditVisibility(event) {
    this.editVisibility = event.target.value;
  }

  @action
  updateEditRecurrenceRule(event) {
    this.editRecurrenceRule = event.target.value;

    if (this.isEditRecurring && !this.editRecurrenceUntil) {
      this.editRecurrenceUntil = defaultRecurrenceUntil(this.editDate);
    }

    if (this.isEditWeeklyRecurrence) {
      this.ensureEditWeeklyBydaySelection();
    }

    if (!this.isEditRecurring) {
      this.editRecurrenceInterval = "1";
      this.editRecurrenceUntil = "";
      this.editRecurrenceByday = "";
    }
  }

  @action
  updateEditRecurrenceInterval(event) {
    this.editRecurrenceInterval = `${normalizedInterval(event.target.value)}`;
  }

  @action
  toggleEditRecurrenceByday(weekday, event) {
    const selected = new Set(this.selectedEditRecurrenceByday);

    if (event.target.checked) {
      selected.add(weekday.code);
    } else {
      selected.delete(weekday.code);
    }

    this.editRecurrenceByday = RECURRENCE_WEEKDAYS
      .map((day) => day.code)
      .filter((code) => selected.has(code))
      .join(",");
  }

  ensureEditWeeklyBydaySelection() {
    if (!this.isEditWeeklyRecurrence || this.editRecurrenceByday) {
      return;
    }

    this.editRecurrenceByday = weekdayCodeForDate(this.editDate);
  }

  @action
  toggleEditRsvpEnabled(event) {
    this.editRsvpEnabled = event.target.checked;
  }

  @action
  updateEditRsvpMode(event) {
    this.editRsvpMode = event.target.value === "external" ? "external" : "native";
  }

  @action
  updateEditExternalRsvpUrl(event) {
    this.editExternalRsvpUrl = event.target.value;
  }

  @action
  toggleEditRsvpAllowInterested(event) {
    this.editRsvpAllowInterested = event.target.checked;
  }

  @action
  async saveEvent(event) {
    event?.preventDefault();

    if (!this.canSaveEdit) {
      return;
    }

    this.isSavingEvent = true;
    this.editError = "";
    this.editNotice = "";

    const customRrule = this.editCustomWeeklyRrule;
    const eventData = {
      title: this.editTitle,
      image_upload_id: this.editImageUploadId,
      starts_at: this.editAllDay
        ? `${this.editDate}T00:00`
        : `${this.editDate} ${this.editStartTime}`,
      ends_at: this.editAllDay
        ? `${nextDayString(this.editDate)}T00:00`
        : `${this.editEndDate} ${this.editEndTime}`,
      timezone: normalizeTimeZone(this.editTimezone, browserTimeZone()),
      location: this.editLocation,
      location_id: this.editSelectedLocationId,
      room_id: this.editRoomId,
      category_id: this.editCategoryId,
      tag_names: this.editTagNamesCsv,
      description: this.editDescription,
      rsvp_enabled: this.editRsvpEnabled,
      rsvp_mode: this.editRsvpEnabled ? this.editRsvpMode : "native",
      external_rsvp_url:
        this.editRsvpEnabled && this.editRsvpMode === "external"
          ? normalizedUrlInput(this.editExternalRsvpUrl)
          : "",
      rsvp_allow_interested:
        this.editRsvpEnabled && this.editRsvpMode !== "external"
          ? this.editRsvpAllowInterested
          : false,
      visibility: visibilityValueForMode(this.editVisibility),
      hidden: hiddenValueForMode(this.editVisibility),
      capacity: this.editCapacity,
      recurrence_rule: customRrule ? "none" : this.editRecurrenceRule,
      recurrence_interval: this.isEditRecurring ? normalizedInterval(this.editRecurrenceInterval) : 1,
      recurrence_until: this.isEditRecurring ? `${this.editRecurrenceUntil}T23:59:59` : "",
      event_roles: this.editEventRoles(),
    };

    if (customRrule) {
      eventData.external_rrule = customRrule;
    } else if (this.editHadSupportedExternalRrule) {
      eventData.external_rrule = "";
    }

    if (this.placesEnabled) {
      eventData.place_id = this.editPlaceId;
    }

    try {
      const response = await ajax(`/calendar/events/${this.event.id}`, {
        type: "PUT",
        data: {
          event: eventData,
        },
      });

      this.event = normalizeEventResponse(response);
      this.isEditing = false;
      this.editNotice = "Event updated.";
      this.resetEditFields();
      this.teardownEditDescriptionUpload();
    } catch (error) {
      this.editError = extractErrorMessage(error, "Event could not be updated. Check the details and try again.");
      popupAjaxError(error);
    } finally {
      this.isSavingEvent = false;
    }
  }

  @action
  async cancelEvent() {
    if (!this.canCancel || this.isCancelingEvent) {
      return;
    }

    this.isCancelingEvent = true;
    this.editError = "";
    this.editNotice = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/cancel`, {
        type: "POST",
      });

      this.event = normalizeEventResponse(response);
      this.editNotice = "Event canceled.";
    } catch (error) {
      this.editError = extractErrorMessage(error, "Event could not be canceled. Try again.");
      popupAjaxError(error);
    } finally {
      this.isCancelingEvent = false;
    }
  }

  @action
  async uncancelEvent() {
    if (!this.canUncancel || this.isUncancelingEvent) {
      return;
    }

    this.isUncancelingEvent = true;
    this.editError = "";
    this.editNotice = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/uncancel`, {
        type: "POST",
      });

      this.event = normalizeEventResponse(response);
      this.editNotice = "Event uncanceled.";
    } catch (error) {
      this.editError = extractErrorMessage(error, "Event could not be uncanceled. Try again.");
      popupAjaxError(error);
    } finally {
      this.isUncancelingEvent = false;
    }
  }

  async updateEventStatus(status, notice) {
    if (!this.canEdit || this.isUpdatingStatus) {
      return;
    }

    this.isUpdatingStatus = true;
    this.editError = "";
    this.editNotice = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}`, {
        type: "PUT",
        data: { event: { status } },
      });

      this.event = normalizeEventResponse(response);
      this.editNotice = notice;
    } catch (error) {
      this.editError = extractErrorMessage(error, "Event status could not be updated. Try again.");
      popupAjaxError(error);
    } finally {
      this.isUpdatingStatus = false;
    }
  }

  @action
  publishEvent() {
    this.updateEventStatus("published", "Event published.");
  }

  @action
  revertEventToDraft() {
    this.updateEventStatus("draft", "Event reverted to draft.");
  }

  @action
  async approveEvent() {
    if (!this.canReviewEvent || this.cannotReviewEvent) {
      return;
    }

    this.isApprovingEvent = true;
    this.editError = "";
    this.editNotice = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/approve`, {
        type: "POST",
      });

      this.event = normalizeEventResponse(response);
      this.editNotice = response.notice || "Event approved.";
    } catch (error) {
      this.editError = extractErrorMessage(error, "Event could not be approved. Try again.");
      popupAjaxError(error);
    } finally {
      this.isApprovingEvent = false;
    }
  }

  @action
  async rejectEvent() {
    if (!this.canReviewEvent || this.cannotReviewEvent) {
      return;
    }

    const reason = window.prompt("Reason for rejection");
    if (!reason || !reason.trim()) {
      return;
    }

    this.isRejectingEvent = true;
    this.editError = "";
    this.editNotice = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/reject`, {
        type: "POST",
        data: { reject_reason: reason.trim() },
      });

      this.event = normalizeEventResponse(response);
      this.editNotice = response.notice || "Event rejected.";
    } catch (error) {
      this.editError = extractErrorMessage(error, "Event could not be rejected. Try again.");
      popupAjaxError(error);
    } finally {
      this.isRejectingEvent = false;
    }
  }

  async loadInvitations() {
    if (!this.showInvitationsPanel || !this.event?.id) {
      return;
    }

    this.isLoadingInvitations = true;
    this.invitationError = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/invitations.json`);
      this.invitations = Array.isArray(response?.invitations) ? response.invitations : [];
    } catch (error) {
      this.invitationError = extractErrorMessage(error, "Invitees could not be loaded.");
      popupAjaxError(error);
    } finally {
      this.isLoadingInvitations = false;
    }
  }

  @action
  updateInviteUsernames(event) {
    this.inviteUsernames = event.target.value;
  }

  @action
  updateInviteGroupNames(event) {
    this.inviteGroupNames = event.target.value;
  }

  @action
  async addInvitations(event) {
    event?.preventDefault();

    if (this.cannotSaveInvitations) {
      return;
    }

    this.isSavingInvitations = true;
    this.invitationNotice = "";
    this.invitationError = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/invitations.json`, {
        type: "POST",
        data: {
          invitation: {
            usernames: this.inviteUsernames,
            group_names: this.inviteGroupNames,
          },
        },
      });

      this.invitations = Array.isArray(response?.invitations) ? response.invitations : [];
      this.inviteUsernames = "";
      this.inviteGroupNames = "";
      const skipped = Array.isArray(response?.skipped) ? response.skipped.length : 0;
      this.invitationNotice =
        skipped > 0 ? `Invitees updated. ${skipped} entries were skipped.` : "Invitees updated.";
    } catch (error) {
      this.invitationError = extractErrorMessage(error, "Invitees could not be updated.");
      popupAjaxError(error);
    } finally {
      this.isSavingInvitations = false;
    }
  }

  @action
  async removeInvitation(invitation) {
    if (!invitation?.id || this.removingInvitationId) {
      return;
    }

    if (!window.confirm(`Remove ${invitationDisplayName(invitation)} from invitees?`)) {
      return;
    }

    this.removingInvitationId = invitation.id;
    this.invitationNotice = "";
    this.invitationError = "";

    try {
      const response = await ajax(
        `/calendar/events/${this.event.id}/invitations/${invitation.id}.json`,
        { type: "DELETE" }
      );
      this.invitations = Array.isArray(response?.invitations) ? response.invitations : [];
      this.invitationNotice = "Invitee removed.";
    } catch (error) {
      this.invitationError = extractErrorMessage(error, "Invitee could not be removed.");
      popupAjaxError(error);
    } finally {
      this.removingInvitationId = null;
    }
  }

  @action
  async createDiscussionTopic() {
    if (!this.canCreateTopic || this.isCreatingTopic) {
      return;
    }

    this.isCreatingTopic = true;
    this.editError = "";
    this.editNotice = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/topic`, {
        type: "POST",
      });

      this.event = normalizeEventResponse(response);
      this.editNotice = "Discussion topic created.";
      this.discussionTopic = null;
      this.discussionPosts = [];
      this.discussionLoadError = "";
      this.discussionReply = "";
      this.discussionReplyError = "";
      this.discussionReplyNotice = "";
    } catch (error) {
      this.editError = extractErrorMessage(error, "Discussion topic could not be created. Try again.");
      popupAjaxError(error);
    } finally {
      this.isCreatingTopic = false;
    }
  }

  @action
  async postDiscussionReply(event) {
    event?.preventDefault();

    if (!this.canSubmitDiscussionReply) {
      return;
    }

    const raw = this.discussionReply.trim();
    this.isSavingDiscussionReply = true;
    this.discussionReplyError = "";
    this.discussionReplyNotice = "";

    try {
      const response = await ajax("/posts.json", {
        type: "POST",
        data: {
          topic_id: this.event.topic_id,
          raw,
        },
      });
      const post = normalizeDiscussionPost(response, raw);

      this.discussionPosts = [...this.discussionPosts, post];
      if (this.discussionTopic) {
        this.discussionTopic = {
          ...this.discussionTopic,
          postsCount: (this.discussionTopic.postsCount || 0) + 1,
          replyCount: (this.discussionTopic.replyCount || 0) + 1,
        };
      }
      this.discussionReply = "";
      this.discussionReplyNotice = "Reply posted.";
    } catch (error) {
      this.discussionReplyError = extractErrorMessage(error, "Reply could not be posted. Try again.");
      popupAjaxError(error);
    } finally {
      this.isSavingDiscussionReply = false;
    }
  }

  @action
  async createChat() {
    if (!this.canCreateChat || this.isCreatingChat) {
      return;
    }

    this.isCreatingChat = true;
    this.editError = "";
    this.editNotice = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/chat`, {
        type: "POST",
      });

      this.event = normalizeEventResponse(response);
      await this.refreshChatChannel();
      await this.refreshChatTranscript();
      this.editNotice = "Chat thread created.";
    } catch (error) {
      this.editError = extractErrorMessage(error, "Chat thread could not be created. Try again.");
      popupAjaxError(error);
    } finally {
      this.isCreatingChat = false;
    }
  }

  async refreshChatChannel() {
    if (!this.event?.chat_channel_id) {
      this.chatChannel = null;
      return;
    }

    try {
      const response = await ajax(`/chat/api/channels/${encodeURIComponent(this.event.chat_channel_id)}`);
      this.chatChannel = normalizeChatChannelResponse(response);
      this.chatChannelLoadError = "";
    } catch {
      this.chatChannel = null;
      this.chatChannelLoadError = "";
    }
  }

  @action
  async refreshChatTranscript() {
    if (!this.event?.chat_channel_id || !this.event?.chat_thread_id || this.isRefreshingChatTranscript) {
      return;
    }

    this.isRefreshingChatTranscript = true;
    this.chatLoadError = "";

    const channelId = encodeURIComponent(this.event.chat_channel_id);
    const threadId = encodeURIComponent(this.event.chat_thread_id);

    try {
      const [threadResponse, messagesResponse] = await Promise.all([
        ajax(`/chat/api/channels/${channelId}/threads/${threadId}`),
        ajax(`/chat/api/channels/${channelId}/threads/${threadId}/messages`, {
          data: {
            page_size: CHAT_TRANSCRIPT_PAGE_SIZE,
            fetch_from_first_message: true,
          },
        }),
      ]);
      const transcript = normalizeChatTranscriptResponse(threadResponse, messagesResponse);

      this.chatThread = transcript.thread;
      this.chatMessages = transcript.messages;
      this.chatCanLoadMorePast = transcript.canLoadMorePast;
      this.chatCanLoadMoreFuture = transcript.canLoadMoreFuture;
    } catch (error) {
      this.chatLoadError = extractErrorMessage(error, "Chat transcript could not be refreshed.");
    } finally {
      this.isRefreshingChatTranscript = false;
    }
  }

  @action
  async toggleChatChannelMembership() {
    if (!this.hasChatChannelControls || this.isUpdatingChatChannelMembership) {
      return;
    }

    this.isUpdatingChatChannelMembership = true;
    this.chatChannelError = "";
    this.chatChannelNotice = "";

    try {
      const channelId = encodeURIComponent(this.chatChannel.id);
      const response = this.isFollowingChatChannel
        ? await ajax(`/chat/api/channels/${channelId}/memberships/me/follows`, { type: "DELETE" })
        : await ajax(`/chat/api/channels/${channelId}/memberships/me`, { type: "POST" });
      const membership = response?.membership || {};

      this.chatChannel = {
        ...this.chatChannel,
        membership,
        following: membership.following === undefined ? !this.isFollowingChatChannel : membership.following === true,
      };
      this.chatChannelNotice = this.isFollowingChatChannel
        ? "Chat channel followed."
        : "Chat channel unfollowed.";
    } catch (error) {
      this.chatChannelError = extractErrorMessage(error, "Chat channel membership could not be updated.");
      popupAjaxError(error);
    } finally {
      this.isUpdatingChatChannelMembership = false;
    }
  }

  @action
  updateRsvpOccurrence(event) {
    this.selectedRsvpOccurrenceKey = event.target.value || "series";
    this.rsvpError = "";
    this.rsvpNotice = "";
  }

  @action
  toggleGoing() {
    this.rsvp(this.isGoingRsvp ? "not_going" : "going");
  }

  @action
  toggleInterested() {
    this.rsvp(this.isInterestedRsvp ? "not_going" : "interested");
  }

  @action
  async rsvp(status) {
    if (!this.canRsvp || this.isSavingRsvp) {
      return;
    }

    this.isSavingRsvp = true;
    this.rsvpError = "";
    this.rsvpNotice = "";

    try {
      const data = { status };
      const occurrence = this.selectedRsvpOccurrence;

      if (occurrence) {
        data.occurrence_key = occurrence.occurrence_key;
        data.occurrence_starts_at = occurrence.starts_at;
        data.occurrence_ends_at = occurrence.ends_at;

        if (occurrence.id) {
          data.occurrence_id = occurrence.id;
        }
      } else {
        data.scope = "series";
      }

      const response = await ajax(`/calendar/events/${this.event.id}/rsvp`, {
        type: "POST",
        data,
      });
      this.event = normalizeEventResponse(response);
      this.rsvpNotice = response?.attendee?.message || "";
      await this.loadAttendees();
    } catch (error) {
      this.rsvpError = extractErrorMessage(error);
      popupAjaxError(error);
    } finally {
      this.isSavingRsvp = false;
    }
  }

  async loadAttendees() {
    if (!this.event?.id) {
      return;
    }

    this.isLoadingAttendees = true;
    this.attendeeLoadError = "";

    try {
      const response = await ajax(`/calendar/events/${this.event.id}/attendees.json`);
      this.attendeeList = Array.isArray(response?.attendees) ? response.attendees : [];
      this.canViewAllAttendeeStatuses = response?.can_view_all_statuses === true;
    } catch (error) {
      this.attendeeLoadError = extractErrorMessage(error, "Attendees could not be loaded.");
    } finally {
      this.isLoadingAttendees = false;
    }
  }
}
